// BETA 15.65 — Combat Test Bench: COMPLETE OVERHAUL (Option A)
//
// Per Dylan: "the COMBAT TEST BENCH should be exactly what is in game already.
// if there needs to be more added to make it so then do it."
//
// PREVIOUSLY: A 2,309-line modal that COVERED the WeaponLab game with a
// bespoke preview canvas reimplementing weapons, bullets, collision, and
// rendering from scratch. 13 known divergences from the real game (bullets
// at 50% speed, orbit ring 38% bigger, no spread/crit/piercing/bouncing,
// red-circle enemies, etc.).
//
// NOW: Side-panel that LIVES NEXT TO the real game. Stat sliders auto-write
// to WEAPON_DATABASE so the actual running game shows changes instantly.
// The "preview" IS the real game. No divergence possible.
//
// What survives:
//   - Weapon picker with three-checkbox system (✓ COPY · ☑ equip · ● select)
//   - All stat sliders + per-stat reset + yellow "modified" indicator
//   - Auto-save overrides to localStorage (persists across reloads)
//   - Notes + design intent textareas per weapon
//   - COPY export (JSON of ✓-checked weapons + modified fields + intent + notes)
//   - Item / Perc sections (read-only stats + ▶ play sound + ▶ trigger effect)
//   - Barrel calibrator embed
//
// What's deleted (lived in the bespoke preview):
//   - PreviewCanvas function (~815 lines)
//   - paused / timeScale / hitbox / lock-on range / fire flash toggles
//   - enemySpawn / simulateRunning toggles (use WeaponLab right sidebar)
//   - orbitRadius slider (real game has fixed ORBIT_RADIUS = 40.5)
//   - mute / sound-on-fire toggles
//   - Live metrics overlay (DPS / accuracy / kills/sec)
//
// Equipped state is now driven by getEquippedWeaponIds() / setWeaponEquipped()
// from weaponLab — same API the WeaponLab right sidebar uses, so both stay
// in sync automatically.

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { WEAPON_DATABASE } from '../game/weapons';
import type { WeaponDefinition } from '../game/weapons';
import { BUNCHA_BULLSHIIII } from '../game/bunchaBullshiiii';
import type { BullshiiiiItem } from '../game/bunchaBullshiiii';
import { PEANUT_PERCS } from '../game/peanutPercs';
import type { PeanutPerc } from '../game/peanutPercs';
import {
  trackPercProc, trackItemProc,
  getEquippedWeaponIds, setWeaponEquipped,
} from '../game/weaponLab';
import {
  spawnPoisonCloud, spawnMorningWoodExplosion, spawnBubbleWrapReflect,
} from '../game/itemEffectSystem';
import {
  playRevolverShot, playRifleShot, playSpatulaSlash, playBasketballBounce,
  playEnemyHit, playDiarrheaGas, playAnusRocket, playTBagSummon,
} from '../game/gameSfx';

interface Props { onClose: () => void; }

type Category = 'weapon' | 'item' | 'perc';

// ─── Audio file map ──────────────────────────────────────────────────────────
const ENTITY_AUDIO: Record<string, { file: string; play?: () => void; note?: string }> = {
  revolver:            { file: '/sounds/revolver_shot.wav',      play: playRevolverShot },
  rpk_machine_gun:     { file: '/sounds/revolver_shot.wav',      play: playRevolverShot, note: 'shares revolver_shot.wav' },
  peanut_rifle:        { file: '/sounds/sniper_shot.wav',        play: playRifleShot },
  peanut_sniper:       { file: '/sounds/sniper_shot.wav',        play: playRifleShot },
  spatula_slash:       { file: '/sounds/spatchula-sound-1.wav',  play: playSpatulaSlash },
  tactical_basketball: { file: '/sounds/basketball_bounce.wav',  play: playBasketballBounce },
  fireball_wand:       { file: '(none mapped)', play: () => playEnemyHit() },
  viking_axe:          { file: '(none mapped)', play: () => playEnemyHit() },
  voogoo_rager_toss:   { file: '(none mapped)', play: () => playEnemyHit() },
  blunderbuss:         { file: '(none mapped)', play: () => playEnemyHit() },
  chad_aura:           { file: '(none — aura is silent)' },
  wpn_ultra_beam:      { file: '(none mapped)' },
  wpn_the_terminator:  { file: '(none mapped)' },
  wpn_ugly_kittens:    { file: '(none mapped)', play: () => playEnemyHit() },
  wpn_karen_drones:    { file: '(none mapped)' },
  wpn_poop_sling:      { file: '(none mapped)' },
  wpn_squirter:        { file: '(none mapped)' },
  wpn_frozen_darts:    { file: '(none mapped)' },
  wpn_dracs_fang:      { file: '(none mapped)', play: () => playEnemyHit() },
  wpn_shitty_cannon:   { file: '(none mapped)' },
  wpn_ex_girlfriend:   { file: '(uses revolver_shot)', play: playRevolverShot },
  wpn_hurray:          { file: '(none mapped)' },
  R08:                 { file: '/sounds/rocket_sound.wav', play: () => playAnusRocket(0, 0), note: 'Anus Rocket' },
  R11:                 { file: '/sounds/gas.wav + /sounds/fart.wav', play: playDiarrheaGas, note: 'Diarrhea Gas' },
  R16:                 { file: '/sounds/t_bag.wav', play: playTBagSummon, note: 'Nutty T-Bag' },
  R10:                 { file: '(uses generic enemy_hit)', play: () => playEnemyHit() },
  R06:                 { file: '(uses generic enemy_hit)', play: () => playEnemyHit() },
  R09:                 { file: '(none — Big Brain is passive)' },
  N13:                 { file: '(none — Not Xanax is reactive)' },
  N14:                 { file: '(none — Not Acid phase is passive)' },
};
function getAudioInfo(entityId: string): { file: string; play?: () => void; note?: string } {
  return ENTITY_AUDIO[entityId] ?? { file: '(no specific sound — uses generic enemy_hit)', play: () => playEnemyHit() };
}

// ─── localStorage helpers ────────────────────────────────────────────────────
const NOTES_KEY = (id: string) => `bench_notes_${id}`;
const INTENT_KEY = (id: string) => `bench_intent_${id}`;
const OVERRIDES_KEY = 'bench_weapon_overrides_v1';
const PRESETS_KEY = 'bench_presets_v1';
const CHECKED_KEY = 'bench_checked_weapons_v1';

function loadStr(key: string): string {
  try { return localStorage.getItem(key) ?? ''; } catch { return ''; }
}
function saveStr(key: string, val: string): void {
  try { localStorage.setItem(key, val); } catch { /* quota */ }
}
function loadJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch { return fallback; }
}
function saveJSON(key: string, val: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* quota */ }
}

// ─── Override system ─────────────────────────────────────────────────────────
// Per-weapon stat overrides persist across page reloads. On modal mount we
// apply them to WEAPON_DATABASE so the running lab game shows the user's
// tuning live. Reset clears the override and reverts to default.

type WeaponOverride = Partial<Record<keyof WeaponDefinition | string, number | boolean | string>>;
type AllOverrides = Record<string, WeaponOverride>;

// Cache of original WEAPON_DATABASE values so we can compute "modified vs default"
// without losing the original after applying overrides.
const _defaultsCache: Record<string, WeaponDefinition> = {};
function snapshotDefaults() {
  if (Object.keys(_defaultsCache).length > 0) return; // already done
  for (const w of WEAPON_DATABASE) {
    // Deep clone so subsequent mutations don't poison the cache
    _defaultsCache[w.id] = JSON.parse(JSON.stringify(w));
  }
}
function getDefault(weaponId: string): WeaponDefinition | undefined {
  return _defaultsCache[weaponId];
}
function applyOverrides(overrides: AllOverrides): void {
  for (const w of WEAPON_DATABASE) {
    const ov = overrides[w.id];
    if (!ov) continue;
    for (const [k, v] of Object.entries(ov)) {
      if (k.startsWith('projectile.')) {
        const pk = k.slice('projectile.'.length) as keyof WeaponDefinition['projectile'];
        (w.projectile as unknown as Record<string, unknown>)[pk as string] = v as unknown;
      } else {
        (w as unknown as Record<string, unknown>)[k] = v as unknown;
      }
    }
  }
}
function resetWeaponToDefault(weaponId: string): void {
  const d = getDefault(weaponId);
  if (!d) return;
  const w = WEAPON_DATABASE.find(x => x.id === weaponId);
  if (!w) return;
  // Restore by overwriting all writable fields from cached default.
  Object.assign(w, JSON.parse(JSON.stringify(d)));
}

// ─── UI styles ───────────────────────────────────────────────────────────────
// BETA 15.65 — Converted from full-screen modal to fixed left-side panel.
// The real game runs in the middle (WeaponLab adjusts GameCanvas paddingLeft).
// Real weapons + real bullets + real enemies — no bespoke preview canvas.
export const STATS_LAB_PANEL_WIDTH = 480;
const panelStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0,
  width: STATS_LAB_PANEL_WIDTH, height: '100vh',
  background: '#1a1f28', borderRight: '2px solid #3a4555',
  fontFamily: '"Share Tech Mono", monospace', color: '#cfd8e3',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  zIndex: 9999,
};
const headerStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #3a4555',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  flex: '0 0 auto',
};
const scrollAreaStyle: React.CSSProperties = {
  flex: '1 1 auto', overflowY: 'auto', padding: '12px 14px',
};
const sectionH: React.CSSProperties = { fontSize: 12, color: '#7a8898', marginTop: 14, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 };
const labelStyle: React.CSSProperties = { fontSize: 11, color: '#8a98a8', display: 'block', marginBottom: 2 };
const inputStyle: React.CSSProperties = {
  background: '#0f1318', color: '#cfd8e3', border: '1px solid #3a4555',
  padding: '4px 6px', fontFamily: 'inherit', fontSize: 12, width: '100%',
};
const btnStyle: React.CSSProperties = {
  padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
  background: '#2a3340', color: '#ffd700', border: '1px solid #ffd700', borderRadius: 4,
};
const playBtnStyle: React.CSSProperties = { ...btnStyle, background: '#1a4a1a', color: '#9fff9f', borderColor: '#3a8a3a' };
const closeBtnStyle: React.CSSProperties = { ...btnStyle, background: '#4a1a1a', color: '#ff9f9f', borderColor: '#8a3a3a' };
const pillBtn = (active: boolean): React.CSSProperties => ({
  padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
  background: active ? '#3a4555' : '#222a34', color: active ? '#ffd700' : '#cfd8e3',
  border: '1px solid ' + (active ? '#ffd700' : '#3a4555'), borderRadius: 4,
  marginRight: 4,
});
const resetIconStyle: React.CSSProperties = {
  background: 'transparent', color: '#7a8898', border: '1px solid #3a4555',
  padding: '0 6px', fontSize: 11, cursor: 'pointer', borderRadius: 3,
  fontFamily: 'inherit',
};

// ─── Sprite cache ────────────────────────────────────────────────────────────
const _spriteCache = new Map<string, HTMLImageElement>();
function getOrLoadSprite(url: string): HTMLImageElement {
  let img = _spriteCache.get(url);
  if (img) return img;
  img = new Image();
  img.src = url;
  _spriteCache.set(url, img);
  return img;
}

// ─── NumberSlider with reset + numeric input + modified indicator ────────────
function StatRow({ label, value, defaultValue, min, max, step, onChange, onReset, tooltip }: {
  label: string; value: number; defaultValue: number;
  min: number; max: number; step: number;
  onChange: (v: number) => void; onReset: () => void;
  tooltip?: string;
}) {
  const modified = Math.abs(value - defaultValue) > 1e-6;
  return (
    <div style={{ marginBottom: 8 }} title={tooltip}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <span style={{ ...labelStyle, marginBottom: 0, flex: 1, color: modified ? '#ffd700' : '#8a98a8' }}>
          {modified ? '● ' : '  '}{label}
        </span>
        <input
          type="number"
          step={step}
          value={value}
          onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) onChange(v); }}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ ...inputStyle, width: 80, fontSize: 11 }}
        />
        <button
          style={resetIconStyle}
          onClick={onReset}
          title={`Reset to default (${defaultValue})`}
          disabled={!modified}
        >↺</button>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onKeyDown={(e) => e.stopPropagation()}
        style={{ width: '100%', accentColor: modified ? '#ffd700' : undefined }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function StatsAndVFXLab({ onClose }: Props) {
  // Snapshot defaults on first render so reset has a target
  snapshotDefaults();

  const [category, setCategory] = useState<Category>('weapon');
  const [overrides, setOverrides] = useState<AllOverrides>(() => loadJSON<AllOverrides>(OVERRIDES_KEY, {}));
  const [, forceRerender] = useState(0);
  const bumpRender = useCallback(() => forceRerender(v => v + 1), []);

  // Apply persisted overrides to WEAPON_DATABASE on mount
  useEffect(() => { applyOverrides(overrides); bumpRender(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const [weaponId, setWeaponId] = useState<string>(WEAPON_DATABASE[0]?.id ?? '');
  const [itemId, setItemId] = useState<string>(BUNCHA_BULLSHIIII[0]?.id ?? '');
  const [percId, setPercId] = useState<string>(PEANUT_PERCS[0]?.id ?? '');

  // BETA 15.65 — Equipped state now reflects the REAL game (via weaponLab API).
  // The real game's equip set is the single source of truth; both this panel
  // and WeaponLab's right sidebar read/write through it so they stay in sync.
  // We tick a 250ms re-render so external equip changes show up here.
  const equipped = getEquippedWeaponIds();
  useEffect(() => {
    const id = window.setInterval(bumpRender, 250);
    return () => window.clearInterval(id);
  }, [bumpRender]);
  const onToggleEquipped = useCallback((id: string) => {
    setWeaponEquipped(id, !getEquippedWeaponIds().includes(id));
    bumpRender();
  }, [bumpRender]);

  // Blue checkmarks for COPY (subset of weapons we want exported)
  const [checked, setChecked] = useState<Set<string>>(() => new Set(loadJSON<string[]>(CHECKED_KEY, [])));
  const setCheckedAndSave = useCallback((next: Set<string>) => {
    setChecked(next);
    saveJSON(CHECKED_KEY, Array.from(next));
  }, []);

  // Search filter for weapon list
  const [search, setSearch] = useState<string>('');

  // Weapon level (1-40) — affects displayed projection of stats
  const [weaponLevel, setWeaponLevel] = useState<number>(1);

  // Escape to close
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const allWeapons = useMemo(() => WEAPON_DATABASE.slice().sort((a, b) => a.name.localeCompare(b.name)), []);
  // BETA 19.7 — filter out shelved items so the Test Bench matches the
  // real-game pool. Without this, the (52) count and the picker list
  // include 8-9 shelved tier-duplicates that chest/shrine/levelup rolls
  // can never offer. Same fix shape as the weaponLab.listAllItems fix
  // in 19.6 — getActiveBullshiiii is the contract for "items in play."
  const allItems = useMemo(() => BUNCHA_BULLSHIIII.filter(i => i.status === 'active').slice().sort((a, b) => a.name.localeCompare(b.name)), []);
  const allPercs = useMemo(() => PEANUT_PERCS.slice().sort((a, b) => a.name.localeCompare(b.name)), []);

  const filteredWeapons = useMemo(() => {
    if (!search) return allWeapons;
    const q = search.toLowerCase();
    return allWeapons.filter(w =>
      w.name.toLowerCase().includes(q) ||
      w.id.toLowerCase().includes(q) ||
      (w.owner?.toLowerCase() ?? '').includes(q),
    );
  }, [allWeapons, search]);

  const selectedEntityId = category === 'weapon' ? weaponId : category === 'item' ? itemId : percId;
  const audio = getAudioInfo(selectedEntityId);

  // Notes + design intent (per-entity localStorage)
  const [notes, setNotes] = useState(() => loadStr(NOTES_KEY(selectedEntityId)));
  const [intent, setIntent] = useState(() => loadStr(INTENT_KEY(selectedEntityId)));
  useEffect(() => { setNotes(loadStr(NOTES_KEY(selectedEntityId))); setIntent(loadStr(INTENT_KEY(selectedEntityId))); }, [selectedEntityId]);
  const onNotesChange = (text: string) => { setNotes(text); saveStr(NOTES_KEY(selectedEntityId), text); };
  const onIntentChange = (text: string) => { setIntent(text); saveStr(INTENT_KEY(selectedEntityId), text); };

  // ─── Mutate weapon stat with auto-save ─────────────────────────────────────
  // BETA 14.95 — value is now `unknown` to support array values (barrels[]).
  // The override storage type was already `number | boolean | string` but the
  // applyOverrides logic just does (w as Record)[k] = v which doesn't care.
  const mutateWeapon = useCallback((wid: string, key: string, value: unknown) => {
    const w = WEAPON_DATABASE.find(x => x.id === wid);
    if (!w) return;
    if (key.startsWith('projectile.')) {
      const pk = key.slice('projectile.'.length);
      (w.projectile as unknown as Record<string, unknown>)[pk] = value;
    } else {
      (w as unknown as Record<string, unknown>)[key] = value;
    }
    setOverrides(prev => {
      const next = { ...prev };
      const wOv: WeaponOverride = { ...(next[wid] ?? {}) };
      (wOv as unknown as Record<string, unknown>)[key] = value;
      next[wid] = wOv;
      saveJSON(OVERRIDES_KEY, next);
      return next;
    });
    bumpRender();
  }, [bumpRender]);
  const resetWeaponStat = useCallback((wid: string, key: string) => {
    const d = getDefault(wid);
    if (!d) return;
    const w = WEAPON_DATABASE.find(x => x.id === wid);
    if (!w) return;
    let defaultValue: unknown;
    if (key.startsWith('projectile.')) {
      const pk = key.slice('projectile.'.length);
      defaultValue = (d.projectile as unknown as Record<string, unknown>)[pk];
      (w.projectile as unknown as Record<string, unknown>)[pk] = defaultValue;
    } else {
      defaultValue = (d as unknown as Record<string, unknown>)[key];
      (w as unknown as Record<string, unknown>)[key] = defaultValue;
    }
    setOverrides(prev => {
      const next = { ...prev };
      if (next[wid]) {
        const wOv = { ...next[wid] };
        delete wOv[key];
        if (Object.keys(wOv).length === 0) delete next[wid];
        else next[wid] = wOv;
        saveJSON(OVERRIDES_KEY, next);
      }
      return next;
    });
    bumpRender();
  }, [bumpRender]);
  const resetWeaponAll = useCallback((wid: string) => {
    resetWeaponToDefault(wid);
    setOverrides(prev => {
      const next = { ...prev };
      delete next[wid];
      saveJSON(OVERRIDES_KEY, next);
      return next;
    });
    bumpRender();
  }, [bumpRender]);

  // ─── COPY: export only blue-checked weapons ────────────────────────────────
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    const out: { tool: string; version: string; timestamp: string; weapons: Array<Record<string, unknown>> } = {
      tool: 'CombatTestBench',
      version: 'BETA 14.81',
      timestamp: new Date().toISOString(),
      weapons: [],
    };
    const checkedArr = Array.from(checked);
    for (const wid of checkedArr) {
      const w = WEAPON_DATABASE.find(x => x.id === wid);
      if (!w) continue;
      out.weapons.push({
        id: w.id,
        name: w.name,
        currentValues: w,            // full current (post-override) stats
        modifiedFields: overrides[wid] ?? {},
        designIntent: loadStr(INTENT_KEY(wid)),
        notes: loadStr(NOTES_KEY(wid)),
        audioFile: getAudioInfo(wid).file,
      });
    }
    if (out.weapons.length === 0 && category === 'weapon') {
      // No checks set — include just the currently-selected weapon
      const w = WEAPON_DATABASE.find(x => x.id === weaponId);
      if (w) {
        out.weapons.push({
          id: w.id, name: w.name,
          currentValues: w,
          modifiedFields: overrides[w.id] ?? {},
          designIntent: loadStr(INTENT_KEY(w.id)),
          notes: loadStr(NOTES_KEY(w.id)),
          audioFile: getAudioInfo(w.id).file,
        });
      }
    }
    try {
      navigator.clipboard.writeText(JSON.stringify(out, null, 2));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard denied */ }
  };

  return (
    <div style={panelStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div style={{ fontSize: 14, color: '#ffd700' }}>🧪 COMBAT TEST BENCH</div>
          <div style={{ fontSize: 9, color: '#7a8898', letterSpacing: 0.5 }}>
            real game runs at right · stat edits live to WEAPON_DATABASE
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 9, color: '#7a8898' }}>
            {Object.keys(overrides).length > 0 ? `${Object.keys(overrides).length} mod` : 'no edits'}
          </span>
          <button style={closeBtnStyle} onClick={onClose}>✕</button>
        </div>
      </div>

      {/* BETA 15.65 — How-to banner: real game is the preview */}
      <div style={{
        padding: '8px 14px', background: '#0f2a1a', borderBottom: '1px solid #2a4a3a',
        fontSize: 10, color: '#9fdaa8', lineHeight: 1.4, flex: '0 0 auto',
      }}>
        ☑ <b>blue equip</b> = put weapon on your real character (right). Edit
        sliders below — bullets / orbit / spread / crits update <b>live</b> in
        the running game. Spawn enemies via the right sidebar.
      </div>

      {/* SCROLL AREA: all controls */}
      <div style={scrollAreaStyle}>
        <div style={{ display: 'flex', gap: 4, marginBottom: 10 }}>
          <button style={pillBtn(category === 'weapon')} onClick={() => setCategory('weapon')}>🔫 WEAPONS ({allWeapons.length})</button>
          <button style={pillBtn(category === 'item')} onClick={() => setCategory('item')}>🎁 ITEMS ({allItems.length})</button>
          <button style={pillBtn(category === 'perc')} onClick={() => setCategory('perc')}>📜 PERCS ({allPercs.length})</button>
        </div>

        {category === 'weapon' && (
          <WeaponSection
            allWeapons={allWeapons}
            filteredWeapons={filteredWeapons}
            search={search}
            onSearch={setSearch}
            selectedId={weaponId}
            onSelect={setWeaponId}
            equipped={equipped}
            onToggleEquipped={onToggleEquipped}
            checked={checked}
            onToggleChecked={(id) => {
              const next = new Set(checked);
              if (next.has(id)) next.delete(id); else next.add(id);
              setCheckedAndSave(next);
            }}
            weaponLevel={weaponLevel}
            onWeaponLevel={setWeaponLevel}
            overrides={overrides}
            mutateWeapon={mutateWeapon}
            resetWeaponStat={resetWeaponStat}
            resetWeaponAll={resetWeaponAll}
            audio={audio}
            notes={notes}
            onNotesChange={onNotesChange}
            intent={intent}
            onIntentChange={onIntentChange}
          />
        )}
        {category === 'item' && (
          <ItemSection allItems={allItems} selectedId={itemId} onSelect={setItemId} audio={audio}
            notes={notes} onNotesChange={onNotesChange} intent={intent} onIntentChange={onIntentChange} />
        )}
        {category === 'perc' && (
          <PercSection allPercs={allPercs} selectedId={percId} onSelect={setPercId} audio={audio}
            notes={notes} onNotesChange={onNotesChange} intent={intent} onIntentChange={onIntentChange} />
        )}

        {/* COPY at bottom */}
        <div style={{ marginTop: 18, padding: 10, background: '#0f1318', border: '1px solid #3a4555', borderRadius: 4 }}>
          <div style={sectionH}>📋 Export</div>
          <div style={{ fontSize: 11, color: '#7a8898', marginBottom: 6 }}>
            Click ✓ checkbox next to each weapon to export. COPY outputs JSON with full current stats + modifiedFields + design intent + notes + audio file. Paste to PS Major in chat to ship for real.
          </div>
          <button style={btnStyle} onClick={onCopy}>
            {copied ? '✓ COPIED!' : `📋 COPY ${checked.size > 0 ? `(${checked.size} checked weapons)` : '(current selection)'}`}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
//  WEAPON SECTION (the big one)
// ═══════════════════════════════════════════════════════════════════════════════
function WeaponSection({
  allWeapons, filteredWeapons, search, onSearch,
  selectedId, onSelect, equipped, onToggleEquipped, checked, onToggleChecked,
  weaponLevel, onWeaponLevel,
  overrides, mutateWeapon, resetWeaponStat, resetWeaponAll,
  audio, notes, onNotesChange, intent, onIntentChange,
}: {
  allWeapons: WeaponDefinition[]; filteredWeapons: WeaponDefinition[];
  search: string; onSearch: (s: string) => void;
  selectedId: string; onSelect: (id: string) => void;
  equipped: string[]; onToggleEquipped: (id: string) => void;
  checked: Set<string>; onToggleChecked: (id: string) => void;
  weaponLevel: number; onWeaponLevel: (n: number) => void;
  overrides: AllOverrides;
  mutateWeapon: (wid: string, key: string, value: unknown) => void;
  resetWeaponStat: (wid: string, key: string) => void;
  resetWeaponAll: (wid: string) => void;
  audio: { file: string; play?: () => void; note?: string };
  notes: string; onNotesChange: (s: string) => void;
  intent: string; onIntentChange: (s: string) => void;
}) {
  const w = allWeapons.find(x => x.id === selectedId);
  if (!w) return <div>No weapon selected</div>;

  const d = getDefault(selectedId);
  const statRow = (label: string, key: string, min: number, max: number, step: number, tooltip?: string) => {
    if (!d) return null;
    const value = (w as unknown as Record<string, number>)[key];
    const defaultValue = (d as unknown as Record<string, number>)[key];
    return (
      <StatRow label={label} value={value} defaultValue={defaultValue}
        min={min} max={max} step={step}
        onChange={(v) => mutateWeapon(selectedId, key, v)}
        onReset={() => resetWeaponStat(selectedId, key)}
        tooltip={tooltip}
      />
    );
  };
  const projRow = (label: string, key: string, min: number, max: number, step: number, tooltip?: string) => {
    if (!d) return null;
    const value = (w.projectile as unknown as Record<string, number>)[key];
    const defaultValue = (d.projectile as unknown as Record<string, number>)[key];
    return (
      <StatRow label={`projectile.${label}`} value={value} defaultValue={defaultValue}
        min={min} max={max} step={step}
        onChange={(v) => mutateWeapon(selectedId, `projectile.${key}`, v)}
        onReset={() => resetWeaponStat(selectedId, `projectile.${key}`)}
        tooltip={tooltip}
      />
    );
  };

  // Project leveled values for context
  const projectedDmg = w.baseDamage + w.damagePerLevel * (weaponLevel - 1);

  return (
    <>
      <div style={sectionH}>🔫 Weapon picker — three checkboxes per weapon: <span style={{ color: '#4cd964' }}>✓ green</span> = include in COPY (multi) · <span style={{ color: '#3399ff' }}>☑ blue</span> = put on orbit circle (multi) · <span style={{ color: '#a855f7' }}>● purple</span> = load into calibrator + show data (single)</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <input
          type="search"
          placeholder="Search weapons by name / id / owner..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ ...inputStyle, flex: 1 }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 4, maxHeight: 240, overflow: 'auto', padding: 6, background: '#0f1318', border: '1px solid #3a4555', borderRadius: 3 }}>
        {filteredWeapons.map(wp => {
          const isSelected = wp.id === selectedId;
          const isEquipped = equipped.includes(wp.id);
          const isChecked = checked.has(wp.id);
          const hasOverride = overrides[wp.id] && Object.keys(overrides[wp.id]).length > 0;
          return (
            <div key={wp.id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: 3, background: isSelected ? '#3d2d55' : 'transparent', border: isSelected ? '1px solid #a855f7' : '1px solid transparent', borderRadius: 3 }}>
              {/* BETA 14.92 — three-checkbox system per user direction:
                  1. GREEN ✓ — include in COPY export (multi)
                  2. BLUE ☑ — put weapon on orbit circle in live preview (multi)
                  3. PURPLE ● — single-select: load this weapon into the
                     barrel calibrator + show all its data (Notes, intent,
                     stat rows, projectile rows, sprite info, etc.). Only
                     one purple at a time. Replaces the previous "click name
                     to select" UX with an explicit visual indicator. */}
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => onToggleChecked(wp.id)}
                title="GREEN ✓ — Include in COPY export (multi)"
                style={{ accentColor: '#4cd964' }}
              />
              <input
                type="checkbox"
                checked={isEquipped}
                onChange={() => onToggleEquipped(wp.id)}
                title="BLUE ☑ — Put on orbit circle in live preview (multi)"
                style={{ accentColor: '#3399ff' }}
              />
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(wp.id)}
                title="PURPLE ● — Load into barrel calibrator + show all data (single)"
                style={{ accentColor: '#a855f7' }}
              />
              {wp.spriteUrl && (
                <img src={wp.spriteUrl} alt="" style={{ maxWidth: 16, maxHeight: 16, objectFit: 'contain', imageRendering: 'pixelated' }} />
              )}
              <span
                style={{ fontSize: 11, flex: 1, cursor: 'pointer', color: isSelected ? '#a855f7' : '#cfd8e3', fontWeight: isSelected ? 'bold' : 'normal' }}
                onClick={() => onSelect(wp.id)}
                title="Click to load this weapon into the calibrator (same as purple ●)"
              >
                {hasOverride && <span style={{ color: '#ffd700' }}>● </span>}
                {wp.name}
              </span>
            </div>
          );
        })}
      </div>

      {/* BETA 14.96 — Sprite controls promoted to the TOP of the section,
          packed in a single compact block under the thumbnail. User: "all
          scaling sliders shouls just be ner the locked visual above soi dont
          have to scroll so low down. should be easy its the first thing i
          fix." Order: thumbnail | metadata | scale slider | rotate slider |
          flip H + V toggles. ALL save to data via mutateWeapon (overrides). */}
      <div style={sectionH}>📐 Sprite calibration (live — saves to data)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'flex-start', background: '#0f1318', border: '1px solid #3a4555', borderRadius: 4, padding: 8 }}>
        {/* Thumbnail (mirrors live state via CSS transform) */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 80 }}>
          {w.spriteUrl && (
            <img
              src={w.spriteUrl}
              alt=""
              style={{
                maxWidth: 80, maxHeight: 80, width: 'auto', height: 'auto',
                objectFit: 'contain', imageRendering: 'pixelated',
                background: '#0a0e14', border: '1px solid #3a4555', padding: 4,
                transform: `scale(${(w as { spriteFacesLeft?: boolean }).spriteFacesLeft ? -1 : 1}, ${(w as { spriteFlipV?: boolean }).spriteFlipV ? -1 : 1}) rotate(${(w.spriteRotationOffset ?? 0) * 180 / Math.PI}deg)`,
                transition: 'transform 0.1s linear',
              }}
            />
          )}
          {audio.play && <button style={{ ...playBtnStyle, fontSize: 10 }} onClick={audio.play}>▶ Sound</button>}
        </div>
        {/* Sliders + flip toggles */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11 }}>
          {/* Sprite Scale */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#8a98a8', minWidth: 90 }}>Sprite scale:</span>
            <input
              type="range" min={0.1} max={3.0} step={0.05}
              value={w.spriteScale ?? 1.0}
              onChange={(e) => mutateWeapon(selectedId, 'spriteScale', parseFloat(e.target.value))}
              onKeyDown={(e) => e.stopPropagation()}
              style={{ flex: 1 }}
            />
            <input
              type="number" min={0.05} max={4} step={0.05}
              value={(w.spriteScale ?? 1.0).toFixed(2)}
              onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) mutateWeapon(selectedId, 'spriteScale', Math.max(0.05, v)); }}
              onKeyDown={(e) => e.stopPropagation()}
              style={{ ...inputStyle, width: 56 }}
            />
            <button style={{ ...resetIconStyle, fontSize: 10 }} onClick={() => mutateWeapon(selectedId, 'spriteScale', 1.0)} title="Reset to 1×">↺</button>
          </div>
          {/* Rotation slider — drives spriteRotationOffset (radians). Live
              orbit sprite rotates by drawAngle -= spriteRotationOffset. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#8a98a8', minWidth: 90 }}>Rotation:</span>
            <input
              type="range" min={-180} max={180} step={1}
              value={Math.round((w.spriteRotationOffset ?? 0) * 180 / Math.PI)}
              onChange={(e) => mutateWeapon(selectedId, 'spriteRotationOffset', parseFloat(e.target.value) * Math.PI / 180)}
              onKeyDown={(e) => e.stopPropagation()}
              style={{ flex: 1 }}
            />
            <input
              type="number" min={-180} max={180} step={1}
              value={Math.round((w.spriteRotationOffset ?? 0) * 180 / Math.PI)}
              onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) mutateWeapon(selectedId, 'spriteRotationOffset', v * Math.PI / 180); }}
              onKeyDown={(e) => e.stopPropagation()}
              style={{ ...inputStyle, width: 56 }}
            />
            <span style={{ color: '#7a8898', minWidth: 14 }}>°</span>
            <button style={{ ...resetIconStyle, fontSize: 10 }} onClick={() => mutateWeapon(selectedId, 'spriteRotationOffset', 0)} title="Reset rotation to 0°">↺</button>
          </div>
          {/* Flip horizontal + vertical toggles — both save to data */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#8a98a8', minWidth: 90 }}>Flip:</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: (w as { spriteFacesLeft?: boolean }).spriteFacesLeft ? '#3a4555' : '#222a34', border: '1px solid ' + ((w as { spriteFacesLeft?: boolean }).spriteFacesLeft ? '#ffd700' : '#3a4555'), borderRadius: 3, cursor: 'pointer', fontSize: 10 }}>
              <input type="checkbox" checked={!!(w as { spriteFacesLeft?: boolean }).spriteFacesLeft}
                onChange={() => mutateWeapon(selectedId, 'spriteFacesLeft', !(w as { spriteFacesLeft?: boolean }).spriteFacesLeft)} />
              ↔ Horizontal (spriteFacesLeft)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: (w as { spriteFlipV?: boolean }).spriteFlipV ? '#3a4555' : '#222a34', border: '1px solid ' + ((w as { spriteFlipV?: boolean }).spriteFlipV ? '#ffd700' : '#3a4555'), borderRadius: 3, cursor: 'pointer', fontSize: 10 }}>
              <input type="checkbox" checked={!!(w as { spriteFlipV?: boolean }).spriteFlipV}
                onChange={() => mutateWeapon(selectedId, 'spriteFlipV', !(w as { spriteFlipV?: boolean }).spriteFlipV)} />
              ↕ Vertical (spriteFlipV)
            </label>
          </div>
          {/* Metadata line, condensed */}
          <div style={{ fontSize: 10, color: '#7a8898', marginTop: 4 }}>
            <code>{w.id}</code> · {w.weaponType} · {w.fireMode} ·{' '}
            <code>{w.spriteUrl}</code>
            {w.projectile.spriteUrl && <> · proj: <code>{w.projectile.spriteUrl}</code></>}
            {' '}· sound: <code>{audio.file}</code>
          </div>
        </div>
        {/* Reset all button */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <button style={{ ...btnStyle, fontSize: 10 }} onClick={() => resetWeaponAll(selectedId)} title="Reset all this weapon's overrides">↺ RESET ALL</button>
        </div>
      </div>

      <div style={sectionH}>📊 Weapon level slider (preview computed values)</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input type="range" min={1} max={Math.max(40, w.maxLevel)} step={1} value={weaponLevel} onChange={(e) => onWeaponLevel(parseInt(e.target.value, 10))} onKeyDown={(e) => e.stopPropagation()} style={{ flex: 1 }} />
        <span style={{ color: '#ffd700', fontSize: 12 }}>Level {weaponLevel} / {w.maxLevel}</span>
        <span style={{ fontSize: 11, color: '#7a8898' }}>
          → projectedDmg ≈ <span style={{ color: '#ffd700' }}>{projectedDmg.toFixed(1)}</span>
        </span>
      </div>

      <div style={sectionH}>🎯 Aim mode (per-weapon)</div>
      <WeaponAimModeToggle weaponId={selectedId} />

      <div style={sectionH}>🎯 Barrel calibrator (click on the sprite to place arrows — each arrow = one barrel = one projectile fired per cycle)</div>
      <div style={{ fontSize: 11, color: '#7a8898', marginBottom: 4 }}>
        💾 Saved to data: <span style={{ color: '#ffd700' }}>arrow positions/rotations</span> (barrels[]),
        <span style={{ color: '#ffd700' }}> barrelLength</span>, <span style={{ color: '#ffd700' }}>barrelOffsetY</span>,
        <span style={{ color: '#ffd700' }}> spriteRotationOffset</span> (from arrow #0). &nbsp;
        🚫 NOT saved: the "Sprite scale" slider under the calibrator canvas — preview only.
        Use the <span style={{ color: '#ffd700' }}>spriteScale</span> StatRow below to save scale.
      </div>
      <BarrelCalibratorEmbedded
        weapon={w}
        onUpdate={(barrels) => {
          if (barrels.length === 0) return;
          // BETA 14.95 — write the FULL barrels array for multi-barrel firing
          // AND backfill the legacy single-barrel fields from arrow #0 so
          // existing code paths (game's getBarrelTipOffset, etc.) keep working.
          mutateWeapon(selectedId, 'barrels', barrels);
          mutateWeapon(selectedId, 'barrelLength', barrels[0].length);
          mutateWeapon(selectedId, 'barrelOffsetY', barrels[0].offsetY);
          mutateWeapon(selectedId, 'spriteRotationOffset', barrels[0].angle);
        }}
      />

      <div style={sectionH}>🎯 Combat stats (in-game data — auto-saved on edit; ● = modified, ↺ = reset)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {statRow('baseDamage',         'baseDamage',         0, 200, 0.5, 'Damage at level 1')}
        {statRow('damagePerLevel',     'damagePerLevel',     0, 50,  0.5, 'Damage added per weapon level')}
        {statRow('baseProjectileCount','baseProjectileCount',1, 20,  1, 'Shots per fire')}
        {statRow('baseReloadTime',     'baseReloadTime',     0.05, 5, 0.01, 'Seconds between fires')}
        {statRow('spreadAngle',        'spreadAngle',        0, 180, 1, 'Cone spread in degrees')}
        {statRow('lockOnRange',        'lockOnRange',        50, 1500, 10, 'Auto-target range in world units')}
        {statRow('baseCritChance',     'baseCritChance',     0, 1, 0.01, '0-1 fraction')}
        {statRow('critChancePerLevel', 'critChancePerLevel', 0, 0.2, 0.005)}
        {statRow('baseCritDamage',     'baseCritDamage',     0, 3, 0.05, 'Multiplier on crit')}
        {statRow('critDamagePerLevel', 'critDamagePerLevel', 0, 0.5, 0.01)}
        {statRow('baseKnockback',      'baseKnockback',      0, 50, 0.5)}
        {statRow('knockbackPerLevel',  'knockbackPerLevel',  0, 5, 0.05)}
        {statRow('burstDelay',         'burstDelay',         0, 1, 0.01)}
        {statRow('reloadTimePerLevel', 'reloadTimePerLevel', -0.5, 0, 0.005, 'Negative = faster per level')}
        {statRow('minReloadTime',      'minReloadTime',      0.05, 2, 0.01, 'Floor on reload')}
        {statRow('projectileCountPerLevel', 'projectileCountPerLevel', 0, 5, 1)}
      </div>

      <div style={sectionH}>🚀 Projectile</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {projRow('speed',         'speed',         50, 2000, 10)}
        {projRow('speedPerLevel', 'speedPerLevel', 0, 100, 1)}
        {projRow('size',          'size',          1, 50, 0.5)}
        {projRow('sizePerLevel',  'sizePerLevel',  0, 5, 0.1)}
        {projRow('lifetime',      'lifetime',      0.05, 10, 0.05)}
        {projRow('piercing',      'piercing',      0, 99, 1)}
        {projRow('piercingPerLevel', 'piercingPerLevel', 0, 1, 0.1)}
        {projRow('bounces',       'bounces',       0, 20, 1)}
        {projRow('bouncesPerLevel','bouncesPerLevel', 0, 1, 0.1)}
        {projRow('bounceRange',   'bounceRange',   0, 1000, 10)}
      </div>

      <div style={sectionH}>🎨 Sprite scale + rotation (live calibrate)</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {statRow('spriteScale',          'spriteScale',          0.05, 4, 0.05, 'Visual size multiplier on the orbital weapon sprite (0.05× = tiny, 4× = huge)')}
        {statRow('spriteRotationOffset', 'spriteRotationOffset', -3.14, 3.14, 0.01, 'Radians; counter-rotates source PNG')}
        {statRow('spriteSize',           'spriteSize',           8, 128, 1, 'Base sprite size in world units')}
        {statRow('barrelLength',         'barrelLength',         0, 100, 0.5, 'World-units from sprite center to barrel tip')}
        {statRow('barrelOffsetY',        'barrelOffsetY',        -30, 30, 0.5, 'Perpendicular offset from barrel axis')}
        {/* BETA 14.94 — orbitSpinSpeed: rad/sec the sprite rotates on its own
            center while orbiting + while in flight (throwables). 0 = static
            (default for projectile bullets). 6 = ~1 rev/sec (default for
            throwables). User: "add this setting on all projectile bullets
            but its set to 0 automatically if it is not a throwable. gives
            me the option to add it to things if i want to." */}
        {statRow('orbitSpinSpeed',       'orbitSpinSpeed',       -20, 20, 0.5, 'Continuous rotation speed (rad/sec) on sprite\'s own center. 0 = no spin. Throwables default 6. Negative = reverse spin.')}
      </div>

      <div style={sectionH}>🌈 VFX colors (read-only — paste back to me to swap)</div>
      <div style={{ fontSize: 11, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        <div>shape: <code>{w.projectile.projectileShape}</code></div>
        <div>color: <span style={{ background: w.projectile.projectileColor, color: '#000', padding: '0 6px' }}>{w.projectile.projectileColor || '(none)'}</span></div>
        <div>glow: <span style={{ background: w.projectile.projectileGlowColor, color: '#000', padding: '0 6px' }}>{w.projectile.projectileGlowColor || '(none)'}</span></div>
        <div>trail: {w.projectile.trailEffect ? <span style={{ background: w.projectile.trailColor, color: '#000', padding: '0 6px' }}>{w.projectile.trailColor || '(none)'}</span> : '(off)'}</div>
      </div>

      <div style={sectionH}>📝 Notes (auto-saved per weapon, accepts spaces)</div>
      <textarea
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder={`Notes for ${selectedId}\n\nWhat's wrong? What should change? What's broken?`}
        style={{ ...inputStyle, height: 80, resize: 'vertical', fontFamily: 'inherit' }}
      />

      <div style={sectionH}>🎯 Design intent (capture WHY values are what they are)</div>
      <textarea
        value={intent}
        onChange={(e) => onIntentChange(e.target.value)}
        onKeyDown={(e) => e.stopPropagation()}
        placeholder="This weapon should feel: heavy / light / satisfying / chaotic / precise..."
        style={{ ...inputStyle, height: 60, resize: 'vertical', fontFamily: 'inherit' }}
      />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  ITEM SECTION (smaller — items mostly have stat modifiers + triggers)
// ═══════════════════════════════════════════════════════════════════════════════
function ItemSection({ allItems, selectedId, onSelect, audio, notes, onNotesChange, intent, onIntentChange }: {
  allItems: BullshiiiiItem[]; selectedId: string; onSelect: (id: string) => void;
  audio: { file: string; play?: () => void; note?: string };
  notes: string; onNotesChange: (s: string) => void;
  intent: string; onIntentChange: (s: string) => void;
}) {
  const i = allItems.find(x => x.id === selectedId);
  if (!i) return <div>No item selected</div>;

  const playEffect = () => {
    const cx = 0, cy = 0;
    if (i.id === 'R11') { spawnPoisonCloud(cx, cy, 1, 1, 1); playDiarrheaGas(); trackItemProc('R11'); }
    else if (i.id === 'R10') { spawnMorningWoodExplosion(cx, cy, 20, 1); trackItemProc('R10'); }
    else if (i.id === 'R06') { spawnBubbleWrapReflect(cx, cy, 30, 1); trackItemProc('R06'); }
    else if (i.id === 'R08') { playAnusRocket(cx, cy); trackItemProc('R08'); }
    else if (i.id === 'R16') { playTBagSummon(); trackItemProc('R16'); }
    else { audio.play?.(); trackItemProc(i.id); }
  };
  const playable = ['R06', 'R08', 'R10', 'R11', 'R16'].includes(i.id) || !!audio.play;

  return (
    <>
      <div style={sectionH}>🎁 Item</div>
      <select value={selectedId} onChange={(e) => onSelect(e.target.value)} style={inputStyle} onKeyDown={(e) => e.stopPropagation()}>
        {allItems.map(it => <option key={it.id} value={it.id}>{it.name} ({it.id}) — {it.rarity}</option>)}
      </select>

      <div style={sectionH}>Sprite + sound</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {i.iconUrl && <img src={i.iconUrl} alt="" style={{ maxWidth: 64, maxHeight: 64, width: 'auto', height: 'auto', objectFit: 'contain', imageRendering: 'pixelated', background: '#0f1318', border: '1px solid #3a4555' }} />}
        <div style={{ fontSize: 11, flex: 1 }}>
          <div><span style={labelStyle}>iconUrl:</span> <code style={{ fontSize: 10 }}>{i.iconUrl}</code></div>
          <div><span style={labelStyle}>sound:</span> <code style={{ fontSize: 10 }}>{audio.file}</code></div>
          <div><span style={labelStyle}>rarity:</span> {i.rarity} · <span style={labelStyle}>pool:</span> {i.starterPool ? 'starter' : 'unlock'} · <span style={labelStyle}>stackable:</span> {i.stackable ? `up to ${i.maxStacks || '∞'}` : 'no'}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {audio.play && <button style={playBtnStyle} onClick={audio.play}>▶ PLAY SOUND</button>}
          {playable && <button style={playBtnStyle} onClick={playEffect}>▶ TRIGGER EFFECT</button>}
        </div>
      </div>

      <div style={sectionH}>Description</div>
      <div style={{ fontSize: 12, padding: 8, background: '#0f1318', border: '1px solid #3a4555', borderRadius: 3 }}>{i.description}</div>

      <div style={sectionH}>Stat modifiers (per stack)</div>
      <div style={{ fontSize: 11, fontFamily: 'monospace', padding: 8, background: '#0f1318', border: '1px solid #3a4555', borderRadius: 3 }}>
        {(i.statModifiers ?? []).map((m, idx) => <div key={idx}>+{m.value}{m.isPercent ? '%' : ''} {m.stat}</div>)}
        {(!i.statModifiers || i.statModifiers.length === 0) && <div style={{ color: '#7a8898' }}>(no modifiers — effects via triggers)</div>}
      </div>

      <div style={sectionH}>📝 Notes</div>
      <textarea value={notes} onChange={(e) => onNotesChange(e.target.value)} onKeyDown={(e) => e.stopPropagation()}
        style={{ ...inputStyle, height: 80, resize: 'vertical', fontFamily: 'inherit' }} />
      <div style={sectionH}>🎯 Design intent</div>
      <textarea value={intent} onChange={(e) => onIntentChange(e.target.value)} onKeyDown={(e) => e.stopPropagation()}
        style={{ ...inputStyle, height: 60, resize: 'vertical', fontFamily: 'inherit' }} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  PERC SECTION (read-only — percs are mostly passive bonuses)
// ═══════════════════════════════════════════════════════════════════════════════
function PercSection({ allPercs, selectedId, onSelect, audio, notes, onNotesChange, intent, onIntentChange }: {
  allPercs: PeanutPerc[]; selectedId: string; onSelect: (id: string) => void;
  audio: { file: string; play?: () => void; note?: string };
  notes: string; onNotesChange: (s: string) => void;
  intent: string; onIntentChange: (s: string) => void;
}) {
  const p = allPercs.find(x => x.id === selectedId);
  if (!p) return <div>No perc selected</div>;
  const forceProc = () => { trackPercProc(p.id); audio.play?.(); };

  return (
    <>
      <div style={sectionH}>📜 Perc</div>
      <select value={selectedId} onChange={(e) => onSelect(e.target.value)} style={inputStyle} onKeyDown={(e) => e.stopPropagation()}>
        {allPercs.map(pe => <option key={pe.id} value={pe.id}>{pe.name} ({pe.id})</option>)}
      </select>

      <div style={sectionH}>Sprite + sound</div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        {p.iconUrl && <img src={p.iconUrl} alt="" style={{ maxWidth: 64, maxHeight: 64, width: 'auto', height: 'auto', objectFit: 'contain', imageRendering: 'pixelated', background: '#0f1318', border: '1px solid #3a4555' }} />}
        <div style={{ fontSize: 11, flex: 1 }}>
          <div><span style={labelStyle}>iconUrl:</span> <code style={{ fontSize: 10 }}>{p.iconUrl}</code></div>
          <div><span style={labelStyle}>sound:</span> <code style={{ fontSize: 10 }}>{audio.file}</code></div>
          <div><span style={labelStyle}>stat:</span> <code>{p.stat}</code> · <span style={labelStyle}>bonus/level:</span> {p.bonusPerLevel}{p.isPercent ? '%' : ''} · <span style={labelStyle}>maxLevel:</span> {p.maxLevel}</div>
          {p.condition && <div style={{ color: '#9fc8ff' }}>condition: <code>{p.condition.type}</code></div>}
          {p.secondaryStat && <div style={{ color: '#ff9999' }}>secondary trade-off: {p.secondaryStat} {p.secondaryBonusPerLevel}{p.secondaryIsPercent ? '%' : ''} per level</div>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {audio.play && <button style={playBtnStyle} onClick={audio.play}>▶ PLAY SOUND</button>}
          <button style={playBtnStyle} onClick={forceProc}>▶ FORCE PROC</button>
        </div>
      </div>

      <div style={sectionH}>Description</div>
      <div style={{ fontSize: 12, padding: 8, background: '#0f1318', border: '1px solid #3a4555', borderRadius: 3 }}>{p.description}</div>
      {p.flavorText && <div style={{ fontSize: 11, color: '#7a8898', fontStyle: 'italic', marginTop: 4 }}>"{p.flavorText}"</div>}

      <div style={sectionH}>📝 Notes</div>
      <textarea value={notes} onChange={(e) => onNotesChange(e.target.value)} onKeyDown={(e) => e.stopPropagation()}
        style={{ ...inputStyle, height: 80, resize: 'vertical', fontFamily: 'inherit' }} />
      <div style={sectionH}>🎯 Design intent</div>
      <textarea value={intent} onChange={(e) => onIntentChange(e.target.value)} onKeyDown={(e) => e.stopPropagation()}
        style={{ ...inputStyle, height: 60, resize: 'vertical', fontFamily: 'inherit' }} />
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  WEAPON AIM MODE TOGGLE — per-weapon orbit/aim switch (BETA 14.83)
// ═══════════════════════════════════════════════════════════════════════════════
// User direction: "add a switch on weapon settings for each weapon to either just
// circle continuously around or the sprite should move around the circle to point
// at an enemy with spin on the weapon itself so its always pointing at its target"
function WeaponAimModeToggle({ weaponId }: { weaponId: string }) {
  const KEY = `bench_weapon_mode_${weaponId}`;
  const [mode, setMode] = useState<'orbit' | 'aim'>(() => {
    try { return (localStorage.getItem(KEY) as 'orbit' | 'aim' | null) ?? 'orbit'; }
    catch { return 'orbit'; }
  });
  useEffect(() => {
    try { return setMode((localStorage.getItem(KEY) as 'orbit' | 'aim' | null) ?? 'orbit'); }
    catch { /* ignore */ }
  }, [KEY]);
  const choose = (m: 'orbit' | 'aim') => {
    setMode(m);
    try { localStorage.setItem(KEY, m); } catch { /* quota */ }
  };
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: mode === 'orbit' ? '#3a4555' : '#222a34', border: '1px solid ' + (mode === 'orbit' ? '#ffd700' : '#3a4555'), borderRadius: 4, cursor: 'pointer' }}>
        <input type="radio" name={`mode-${weaponId}`} checked={mode === 'orbit'} onChange={() => choose('orbit')} />
        🔄 Orbit (circle continuously)
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: mode === 'aim' ? '#3a4555' : '#222a34', border: '1px solid ' + (mode === 'aim' ? '#ffd700' : '#3a4555'), borderRadius: 4, cursor: 'pointer' }}>
        <input type="radio" name={`mode-${weaponId}`} checked={mode === 'aim'} onChange={() => choose('aim')} />
        🎯 Aim (rotate to face nearest enemy + spin to track)
      </label>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
//  BARREL CALIBRATOR EMBEDDED — click-to-place arrow + real-time preview update
// ═══════════════════════════════════════════════════════════════════════════════
// User direction: "missing the large photo of barrel with the multi barrel thing
// we had so i can place the red dot which instead should be an arrow instead so
// it also calculates direction on the tips of the barrels. should change the
// visual locked on the top in real time the simulation to the exact spot i place
// the barrel arrow tip out for the gun."
//
// Click on the sprite → arrow placed from sprite center to click point. Arrow
// tip = barrel tip; angle = spriteRotationOffset; distance = barrelLength.
// Multiple clicks add multiple arrows (multi-barrel weapons). UNDO removes last.
// CLEAR removes all. Live preview at top of Lab updates IMMEDIATELY when
// barrelLength + spriteRotationOffset are mutated via onUpdate callback.
function BarrelCalibratorEmbedded({ weapon, onUpdate }: {
  weapon: WeaponDefinition;
  // BETA 14.95 — onUpdate now receives the FULL barrels array for multi-barrel
  // weapons. Each arrow placed becomes one barrel. The parent writes barrels[]
  // to weapon.barrels and uses arrow #0 to backfill legacy barrelLength /
  // barrelOffsetY / spriteRotationOffset for backwards compat.
  // spriteScale is NOT propagated — it's preview-only per user direction:
  // "i assume the scale under the barel photo part is just for my eyes and
  // not being saved in data."
  onUpdate: (barrels: Array<{ length: number; offsetY: number; angle: number }>) => void;
}) {
  const CANVAS_W = 720;
  const CANVAS_H = 360;
  const CENTER_X = CANVAS_W / 2;
  const CENTER_Y = CANVAS_H / 2;
  const SPRITE_DISPLAY_PX = 220; // visible sprite height in canvas pixels
  const KEY_ARROWS = `bench_barrel_arrows_${weapon.id}`;
  const KEY_SCALE  = `bench_barrel_scale_${weapon.id}`;

  // BETA 14.84 — arrow has INDEPENDENT position + rotation. User: "arrow shouldnt
  // be connected to sprite center right? i just want to rotate and place an arrow
  // and be able to do multiple if needed. see how due to it locked to center the
  // arrow is not on the barrel showing trajectory? its angled..."
  // dx,dy = pixel offset from sprite center (where the arrow tip sits).
  // angle = the direction the arrow POINTS (the barrel pointing direction),
  // independently controllable via per-arrow slider.
  interface Arrow { dx: number; dy: number; angle: number; }
  const [arrows, setArrows] = useState<Arrow[]>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(KEY_ARROWS) ?? '[]') as Array<{ dx: number; dy: number; angle?: number }>;
      // Backfill `angle` for arrows saved by BETA 14.83 which used center→tip implicit angle
      return stored.map(a => ({
        dx: a.dx,
        dy: a.dy,
        angle: typeof a.angle === 'number' ? a.angle : Math.atan2(a.dy, a.dx),
      }));
    } catch { return []; }
  });
  const [scale, setScale] = useState<number>(() => {
    try { return parseFloat(localStorage.getItem(KEY_SCALE) ?? '1.0') || 1.0; }
    catch { return 1.0; }
  });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [, forceRender] = useState(0);

  // BETA 14.95 — UNIFIED save+reload effect with weapon-id ref guard. User:
  // "whenever i put an arrow on a barrel. on a weapon and im done. i go to a
  // new weapon. it is showing the same arrow i did last time and its not
  // saving for each weapon."
  //
  // Root cause of the bug: the previous code had TWO separate effects with
  // KEY_ARROWS / KEY_SCALE in their dependency arrays. When weapon.id changed:
  //   1. KEY changed → save effect re-fired with NEW key + OLD arrows state
  //      (state hadn't reloaded yet). This wrote the OLD weapon's arrows to
  //      the NEW weapon's localStorage key, clobbering its saved data.
  //   2. The reload effect ran in the same render and tried to restore from
  //      the new key, but it was already corrupted.
  //
  // Fix: combine both into ONE effect, guarded by a ref tracking the
  // last-loaded weapon id. On weapon change → reload + skip save (state is
  // stale, the next render will save with the right state). Otherwise →
  // save + propagate to weapon definition via onUpdate.
  //
  // Also BETA 14.95: send the FULL arrows array as barrels[] (multi-barrel
  // weapons fire one projectile per barrel per cycle). spriteScale is NOT
  // sent anymore — preview-only per user direction.
  const prevWeaponIdRef = useRef<string>('__init__');
  useEffect(() => {
    if (prevWeaponIdRef.current !== weapon.id) {
      // === Weapon just changed: reload + skip save this render ===
      try {
        const stored = JSON.parse(localStorage.getItem(KEY_ARROWS) ?? '[]') as Array<{ dx: number; dy: number; angle?: number }>;
        const restored: Arrow[] = stored.map(a => ({
          dx: a.dx,
          dy: a.dy,
          angle: typeof a.angle === 'number' ? a.angle : Math.atan2(a.dy, a.dx),
        }));
        setArrows(restored);
        const s = parseFloat(localStorage.getItem(KEY_SCALE) ?? '1.0') || 1.0;
        setScale(s);
      } catch { /* ignore */ }
      prevWeaponIdRef.current = weapon.id;
      return; // The next render (with reloaded state) will save normally.
    }

    // === Same weapon: persist + propagate ===
    try {
      localStorage.setItem(KEY_ARROWS, JSON.stringify(arrows));
      localStorage.setItem(KEY_SCALE, String(scale));
    } catch { /* quota */ }

    if (arrows.length > 0) {
      const pxToWorld = (weapon.spriteSize ?? 32) / SPRITE_DISPLAY_PX;
      const barrels = arrows.map(a => {
        const cosA = Math.cos(a.angle);
        const sinA = Math.sin(a.angle);
        const projAlong = cosA * a.dx + sinA * a.dy;
        const projPerp  = -sinA * a.dx + cosA * a.dy;
        return {
          length: Math.round(Math.max(0, projAlong * pxToWorld) * 10) / 10,
          offsetY: Math.round(projPerp * pxToWorld * 10) / 10,
          angle: Math.round(a.angle * 100) / 100,
        };
      });
      onUpdate(barrels);
    }
  }, [arrows, scale, weapon.id, weapon.spriteSize, KEY_ARROWS, KEY_SCALE, onUpdate]);

  // Paint
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rafId = 0;
    const tick = () => {
      ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      ctx.fillStyle = '#0a0e14';
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);

      // Grid (every 20px)
      for (let x = 0; x <= CANVAS_W; x += 20) {
        ctx.strokeStyle = x % 100 === 0 ? 'rgba(80, 200, 255, 0.18)' : 'rgba(80, 200, 255, 0.07)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x + 0.5, 0); ctx.lineTo(x + 0.5, CANVAS_H); ctx.stroke();
      }
      for (let y = 0; y <= CANVAS_H; y += 20) {
        ctx.strokeStyle = y % 100 === 0 ? 'rgba(80, 200, 255, 0.18)' : 'rgba(80, 200, 255, 0.07)';
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, y + 0.5); ctx.lineTo(CANVAS_W, y + 0.5); ctx.stroke();
      }

      // Sprite at center
      if (weapon.spriteUrl) {
        const img = getOrLoadSprite(weapon.spriteUrl);
        if (img && img.complete && img.naturalWidth > 0) {
          const sprH = SPRITE_DISPLAY_PX * scale;
          const aspect = img.naturalWidth / img.naturalHeight;
          const sprW = sprH * aspect;
          ctx.save();
          ctx.translate(CENTER_X, CENTER_Y);
          ctx.drawImage(img, -sprW / 2, -sprH / 2, sprW, sprH);
          ctx.restore();
          // Bounding box (cyan dashed)
          ctx.strokeStyle = 'rgba(127, 220, 255, 0.4)';
          ctx.setLineDash([4, 4]);
          ctx.strokeRect(CENTER_X - sprW / 2, CENTER_Y - sprH / 2, sprW, sprH);
          ctx.setLineDash([]);
        }
      }

      // Center crosshair
      ctx.strokeStyle = '#00ffff';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(CENTER_X - 14, CENTER_Y); ctx.lineTo(CENTER_X + 14, CENTER_Y);
      ctx.moveTo(CENTER_X, CENTER_Y - 14); ctx.lineTo(CENTER_X, CENTER_Y + 14);
      ctx.stroke();
      ctx.fillStyle = '#00ffff';
      ctx.font = '11px "Share Tech Mono", monospace';
      ctx.textAlign = 'left';
      ctx.fillText('SPRITE CENTER', CENTER_X + 16, CENTER_Y - 6);

      // BETA 14.84 — arrows now have INDEPENDENT position + rotation. Each
      // arrow is drawn AT its (dx, dy) position, pointing in its own .angle
      // direction (NOT from center). The arrow has a LENGTH (constant 70px)
      // and the TIP is at (tipX, tipY) — which IS the projectile spawn point.
      // Tail = tip - length × (cos(angle), sin(angle)).
      const ARROW_LENGTH = 70;
      const ARROW_HEAD = 14;
      arrows.forEach((a, i) => {
        const tipX = CENTER_X + a.dx;
        const tipY = CENTER_Y + a.dy;
        const tailX = tipX - ARROW_LENGTH * Math.cos(a.angle);
        const tailY = tipY - ARROW_LENGTH * Math.sin(a.angle);

        // Light dashed line from sprite center to tip — REFERENCE only, no
        // longer drives angle. Helps user see how far from center the tip is.
        ctx.strokeStyle = 'rgba(127, 220, 255, 0.25)';
        ctx.setLineDash([3, 3]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(CENTER_X, CENTER_Y);
        ctx.lineTo(tipX, tipY);
        ctx.stroke();
        ctx.setLineDash([]);

        // The actual arrow: tail → tip, in arrow's own direction
        ctx.strokeStyle = i === 0 ? '#ffd700' : '#cc6633';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(tipX, tipY);
        ctx.stroke();

        // Arrowhead at tip, oriented along arrow.angle
        ctx.fillStyle = i === 0 ? '#ffd700' : '#cc6633';
        ctx.beginPath();
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(tipX - ARROW_HEAD * Math.cos(a.angle - Math.PI / 6), tipY - ARROW_HEAD * Math.sin(a.angle - Math.PI / 6));
        ctx.lineTo(tipX - ARROW_HEAD * Math.cos(a.angle + Math.PI / 6), tipY - ARROW_HEAD * Math.sin(a.angle + Math.PI / 6));
        ctx.closePath();
        ctx.fill();

        // Tail handle (small circle so user can see where the arrow starts)
        ctx.fillStyle = i === 0 ? 'rgba(255, 215, 0, 0.6)' : 'rgba(204, 102, 51, 0.6)';
        ctx.beginPath();
        ctx.arc(tailX, tailY, 4, 0, Math.PI * 2);
        ctx.fill();

        // Number label at tip
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 12px "Share Tech Mono", monospace';
        ctx.textAlign = 'center';
        ctx.fillText(String(i + 1), tipX + 16, tipY - 6);

        // Top-left list readout
        const angDeg = (a.angle * 180 / Math.PI).toFixed(0);
        const dist = Math.hypot(a.dx, a.dy).toFixed(0);
        ctx.fillStyle = '#7a8898';
        ctx.font = '10px "Share Tech Mono", monospace';
        ctx.textAlign = 'left';
        ctx.fillText(`#${i + 1}: pos=(${a.dx.toFixed(0)},${a.dy.toFixed(0)})  dist=${dist}px  rot=${angDeg}°`, 8, 14 + i * 14);
      });

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [arrows, scale, weapon.spriteUrl, weapon.id]);

  const onClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = ((e.clientX - rect.left) / rect.width) * CANVAS_W;
    const cy = ((e.clientY - rect.top) / rect.height) * CANVAS_H;
    const dx = cx - CENTER_X;
    const dy = cy - CENTER_Y;
    // BETA 14.86 — arrow defaults to 0° (RIGHT) on placement. User: "arrow
    // starts angle for some reason when i place it..." — they want a clean
    // horizontal arrow they can then rotate manually. Click point = arrow TIP.
    setArrows(prev => [...prev, { dx, dy, angle: 0 }]);
    forceRender(v => v + 1);
  };
  const undo = () => setArrows(prev => prev.slice(0, -1));
  const clear = () => setArrows([]);
  const setArrowAngle = (idx: number, angleRad: number) => {
    setArrows(prev => prev.map((a, i) => i === idx ? { ...a, angle: angleRad } : a));
  };
  const setArrowFromCenter = (idx: number) => {
    setArrows(prev => prev.map((a, i) => i === idx ? { ...a, angle: Math.atan2(a.dy, a.dx) } : a));
  };
  // BETA 14.86 — move arrow forward/back along its OWN pointing direction.
  // User: "give me a slider to be able to move the arrow forward and back."
  // Positive offset = forward (toward arrow tip direction); negative = backward.
  const moveArrowAlongDirection = (idx: number, offsetPx: number) => {
    setArrows(prev => prev.map((a, i) => {
      if (i !== idx) return a;
      const cosA = Math.cos(a.angle);
      const sinA = Math.sin(a.angle);
      return { ...a, dx: a.dx + cosA * offsetPx, dy: a.dy + sinA * offsetPx };
    }));
  };

  // Primary arrow readouts (computed in PNG-pixel frame, projected onto arrow's
  // own pointing direction). Matches the engine's render math exactly.
  const primary = arrows[0];
  const cosA = primary ? Math.cos(primary.angle) : 0;
  const sinA = primary ? Math.sin(primary.angle) : 0;
  const projAlong = primary ? cosA * primary.dx + sinA * primary.dy : 0;
  const projPerp  = primary ? -sinA * primary.dx + cosA * primary.dy : 0;
  const pxToWorld = (weapon.spriteSize ?? 32) / SPRITE_DISPLAY_PX;
  const blWorld = Math.max(0, projAlong * pxToWorld);
  const boyWorld = projPerp * pxToWorld;

  return (
    <>
      <div style={{ fontSize: 11, color: '#7a8898', marginBottom: 4 }}>
        Click on the sprite at the BARREL TIP — the arrow appears AT that spot. Then use
        the per-arrow ROTATION slider below to point it ALONG the barrel (independent of
        position). Multiple arrows = multi-barrel. The FIRST arrow drives live preview\'s
        barrelLength + barrelOffsetY + spriteRotationOffset.
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        onClick={onClick}
        style={{ width: '100%', maxWidth: CANVAS_W, height: 'auto', border: '1px solid #3a4555', borderRadius: 4, cursor: 'crosshair', display: 'block' }}
      />
      <div style={{ display: 'flex', gap: 12, marginTop: 6, alignItems: 'center', fontSize: 11 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
          {/* BETA 14.90 — slider range now -50 to +50 (% delta from 1.0×) per
              user direction: "let me go to -50 to +50 in size. lowest i can
              go is zero in size and sprite still too big". Slider value is
              displayed as signed percent; underlying scale = 1 + value/100,
              so -50 = 0.5×, 0 = 1.0×, +50 = 1.5×. The 0 reset gives the
              natural sprite size; user drags negative to shrink. For more
              extreme scaling, use the spriteScale StatRow (now 0.05–4.00). */}
          <span style={{ color: '#8a98a8' }}>Sprite scale</span>
          <input type="range" min={-50} max={50} step={1}
            value={Math.round((scale - 1) * 100)}
            onChange={(e) => {
              const pct = parseFloat(e.target.value);
              setScale(Math.max(0.05, 1 + pct / 100));
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{ flex: 1 }} />
          <span style={{ color: '#ffd700', minWidth: 60 }}>
            {(() => { const pct = Math.round((scale - 1) * 100); return `${pct >= 0 ? '+' : ''}${pct}% (${scale.toFixed(2)}×)`; })()}
          </span>
          <button
            style={{ ...resetIconStyle, fontSize: 10 }}
            onClick={() => setScale(1.0)}
            title="Reset to 0% (natural sprite size, 1.00×)"
          >↺</button>
        </label>
        <button style={btnStyle} onClick={undo} disabled={arrows.length === 0}>↩ Undo last</button>
        <button style={btnStyle} onClick={clear} disabled={arrows.length === 0}>🗑 Clear all</button>
      </div>

      {arrows.length > 0 && (
        <div style={{ marginTop: 8, padding: 8, background: '#0f1318', border: '1px solid #3a4555', borderRadius: 3 }}>
          <div style={{ fontSize: 11, color: '#7a8898', marginBottom: 4 }}>Per-arrow rotation:</div>
          {arrows.map((a, i) => {
            const deg = (a.angle * 180 / Math.PI);
            return (
              <div key={i} style={{ marginBottom: 8, padding: '6px 8px', background: i === 0 ? 'rgba(255, 215, 0, 0.05)' : 'rgba(204, 102, 51, 0.05)', borderRadius: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 11 }}>
                  <span style={{ color: i === 0 ? '#ffd700' : '#cc6633', minWidth: 24 }}>#{i + 1}</span>
                  <span style={{ color: '#8a98a8', minWidth: 50 }}>Rotate:</span>
                  <input
                    type="range" min={-180} max={180} step={1}
                    value={Math.round(deg)}
                    onChange={(e) => setArrowAngle(i, parseFloat(e.target.value) * Math.PI / 180)}
                    onKeyDown={(e) => e.stopPropagation()}
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number" min={-180} max={180} step={1}
                    value={Math.round(deg)}
                    onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v)) setArrowAngle(i, v * Math.PI / 180); }}
                    onKeyDown={(e) => e.stopPropagation()}
                    style={{ ...inputStyle, width: 60 }}
                  />
                  <span style={{ color: '#7a8898', minWidth: 12 }}>°</span>
                  <button
                    style={{ ...resetIconStyle, fontSize: 10 }}
                    onClick={() => setArrowFromCenter(i)}
                    title="Reset rotation to the angle from sprite center to arrow tip"
                  >from center</button>
                </div>
                {/* BETA 14.86 — forward/back slider per arrow. User: "give me a
                    slider to be able to move the arrow forward and back." Moves
                    along arrow's OWN pointing direction. -50 to +50 px per drag. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                  <span style={{ color: '#8a98a8', minWidth: 80 }}>Move (along ↑):</span>
                  <button style={{ ...resetIconStyle, fontSize: 10 }} onClick={() => moveArrowAlongDirection(i, -10)} title="Back 10 px">← -10</button>
                  <button style={{ ...resetIconStyle, fontSize: 10 }} onClick={() => moveArrowAlongDirection(i, -1)} title="Back 1 px">← -1</button>
                  <button style={{ ...resetIconStyle, fontSize: 10 }} onClick={() => moveArrowAlongDirection(i, 1)} title="Forward 1 px">+1 →</button>
                  <button style={{ ...resetIconStyle, fontSize: 10 }} onClick={() => moveArrowAlongDirection(i, 10)} title="Forward 10 px">+10 →</button>
                  <span style={{ color: '#7a8898', flex: 1 }}>
                    pos=({a.dx.toFixed(0)}, {a.dy.toFixed(0)})  dist={Math.hypot(a.dx, a.dy).toFixed(0)}px
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 6, fontSize: 11, color: '#cfd8e3' }}>
        {arrows.length === 0 ? (
          <span style={{ color: '#7a8898' }}>No arrows placed yet — click the sprite above.</span>
        ) : (
          <>
            <span style={{ color: '#ffd700' }}>Primary barrel (#1):</span>{' '}
            barrelLength = <code>{blWorld.toFixed(1)}wu</code>{' '}
            barrelOffsetY = <code>{boyWorld.toFixed(1)}wu</code>{' '}
            spriteRotationOffset = <code>{(primary.angle * 180 / Math.PI).toFixed(0)}°</code>{' '}
            spriteScale = <code>{scale.toFixed(2)}×</code>
            {arrows.length > 1 && <span style={{ color: '#7a8898' }}> · {arrows.length - 1} additional arrow(s) for multi-barrel reference</span>}
          </>
        )}
      </div>
    </>
  );
}
